import { useSyncExternalStore } from 'react';
import accessService from '../services/accessService';
import { decodeToken, getToken, getUserId } from './tokenUtils';

const STORAGE_PREFIX = 'rnr_access:';

const EMPTY = Object.freeze({
  permissions: [],
  featureFlags: [],
  platforms: [],
  routeIds: [],
  roleNames: [],
  tierCodes: [],
  defaultLandingPath: undefined,
});

export const normalizeToArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((entry) => String(entry));

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.map((entry) => String(entry));
      } catch {
        return [value];
      }
    }
  }

  return [String(value)];
};

const shape = (source) => ({
  permissions: normalizeToArray(source?.permissions),
  featureFlags: normalizeToArray(source?.featureFlags),
  platforms: normalizeToArray(source?.platforms),
  routeIds: normalizeToArray(source?.routeIds),
  roleNames: normalizeToArray(source?.roleNames),
  tierCodes: normalizeToArray(source?.tierCodes),
  defaultLandingPath: source?.defaultLandingPath ?? undefined,
});

const storageKey = () => {
  const userId = getUserId();
  return userId ? `${STORAGE_PREFIX}${userId}` : null;
};

const readCache = () => {
  const key = storageKey();
  if (!key) return null;

  try {
    const raw = localStorage.getItem(key);
    return raw ? shape(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

const writeCache = (access) => {
  const key = storageKey();
  if (!key) return;

  try {
    localStorage.setItem(key, JSON.stringify(access));
  } catch {
    // A full or unavailable quota costs us a warm start, nothing more.
  }
};

let current = null;
const listeners = new Set();

const emit = () => listeners.forEach((listener) => listener());

const seed = () => {
  const cached = readCache();
  if (cached) return cached;

  if (!getToken()) return EMPTY;

  const claims = decodeToken();
  return claims ? shape(claims) : EMPTY;
};

export const getAccess = () => {
  if (!current) current = seed();
  return current;
};

export const refreshAccess = async () => {
  try {
    const resolved = await accessService.getMe();
    if (!resolved) return getAccess();

    current = shape(resolved);
    writeCache(current);
    emit();
    return current;
  } catch (error) {
    console.warn('Access refresh failed; falling back to cached/token access', error);
    return getAccess();
  }
};

/** Drop everything on logout so the next user never inherits these grants. */
export const clearAccess = () => {
  const key = storageKey();
  current = null;

  if (key) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Could not clear cached access', error);
    }
  }

  emit();
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Re-renders the caller when a refresh lands. */
export const useAccess = () => useSyncExternalStore(subscribe, getAccess, getAccess);
